import type {
  HomeSurfaceState,
  PromptsState,
  ReviewPromptState,
  SlugSurfaceState,
  UiState,
} from '@repo/api';

const LOCAL_STORAGE_KEY = 'openigloo_ui_state';

/**
 * Thresholds each prompt type fires at, per trigger surface. A surface
 * with no entry never fires that prompt — that absence is the switch.
 * Kept in one place so the provider + hook agree.
 *
 * `building` and `owner` both fire the REVIEW prompt on the first view,
 * with the same copy. `building` briefly fired the report prompt instead;
 * the report plumbing for the surface is left intact (ReportSurface still
 * includes 'building', buckets still purge) so switching back is just
 * moving the number between the two maps.
 */
export const PROMPT_THRESHOLDS: {
  review: Partial<Record<TriggerPageType, number>>;
  report: Partial<Record<ReportSurface, number>>;
  appDownload: Partial<Record<AppDownloadSurface, number>>;
} = {
  review: {
    unit: 3,
    home: 2,
    building: 1,
    owner: 1,
    pseo: 1,
  },
  report: {
    unit: 2,
  },
  appDownload: {
    home: 1,
  },
};

/**
 * Home page refresh debounce — a home view within N ms of the previous
 * one doesn't count as a separate view (avoids treating a refresh as
 * two views).
 */
export const HOME_VIEW_DEBOUNCE_MS = 30_000;

export type PromptType = 'review' | 'report' | 'appDownload';
export type TriggerPageType = 'unit' | 'home' | 'building' | 'owner' | 'pseo';

/** Surfaces that fire the report prompt. */
export type ReportSurface = Extract<TriggerPageType, 'unit' | 'building'>;

/** Surfaces that fire the app-download prompt. */
export type AppDownloadSurface = Extract<TriggerPageType, 'home'>;

/**
 * All trigger surfaces the review prompt supports. Exported so the
 * provider + purge helpers can enumerate them consistently.
 */
export const REVIEW_SURFACES: TriggerPageType[] = ['unit', 'home', 'building', 'owner', 'pseo'];

/** All trigger surfaces the report prompt supports. */
export const REPORT_SURFACES: ReportSurface[] = ['unit', 'building'];

/** All trigger surfaces the app-download prompt supports. */
export const APP_DOWNLOAD_SURFACES: AppDownloadSurface[] = ['home'];

export function isReportSurface(pageType: TriggerPageType): pageType is ReportSurface {
  return pageType === 'unit' || pageType === 'building';
}

export function isAppDownloadSurface(pageType: TriggerPageType): pageType is AppDownloadSurface {
  return pageType === 'home';
}

/**
 * True when the review prompt for this specific surface has already
 * been shown for this user.
 */
export function isReviewShownForSurface(
  prompts: PromptsState | undefined,
  surface: TriggerPageType,
): boolean {
  return Boolean(prompts?.review?.[surface]?.shown);
}

/**
 * True when the report prompt for this specific surface has already
 * been shown. Per-surface so the unit and building report prompts
 * never suppress one another.
 */
export function isReportShownForSurface(
  prompts: PromptsState | undefined,
  surface: ReportSurface,
): boolean {
  return Boolean(prompts?.report?.[surface]?.shown);
}

/**
 * True when the app-download prompt for this surface has already been
 * shown.
 */
export function isAppDownloadShownForSurface(
  prompts: PromptsState | undefined,
  surface: AppDownloadSurface,
): boolean {
  return Boolean(prompts?.appDownload?.[surface]?.shown);
}

/**
 * Viewport gate for the app-download prompt. It's mobile-web only, so
 * it must never open above the `md` breakpoint — the same 768px cut as
 * ResponsiveModal's sheet/modal switch.
 *
 * Checked when the threshold is crossed rather than at render time so
 * a desktop view never burns the one-shot `shown` flag or occupies the
 * single active-prompt slot.
 */
export function isMobileWebViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
}

/**
 * Read the full UiState from localStorage. Returns empty object if
 * nothing stored or if parse fails. Never throws.
 *
 * Silently drops any legacy flat-shape prompts blob — the old shape
 * used sibling `counters` / `seen` / `shown` collections. We ship
 * with fresh state rather than migrate (few users have prompt data
 * yet, and the shape changed in a way that's not worth the code).
 */
export function readLocalUiState(): UiState {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const state = parsed as UiState;
    // Drop legacy prompt data — old flat shape (`counters` /
    // `seen` / `shown` at prompts root) is incompatible with the
    // nested-per-surface layout.
    if (state.prompts && isLegacyPromptsShape(state.prompts)) {
      const { prompts, ...rest } = state;
      return rest;
    }
    return state;
  } catch {
    return {};
  }
}

/**
 * Write the full UiState to localStorage. Silent on failure (private
 * mode, quota exceeded, etc.) — never breaks the app.
 */
export function writeLocalUiState(state: UiState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // no-op — best effort
  }
}

/**
 * Merge two UiState blobs. Only the `prompts` sub-tree needs merging
 * for now; other keys shallow-merge (last one wins per key).
 *
 * Merge rules for `prompts`:
 * - `count` → MAX (conservative; never fires prompt early)
 * - `seen` → set union (dedupe)
 * - `last_view_at` → later timestamp wins
 * - `shown` → OR (once shown anywhere, stays shown)
 */
export function mergeUiState(a: UiState, b: UiState): UiState {
  const aPromptsLegacy = a.prompts && isLegacyPromptsShape(a.prompts);
  const bPromptsLegacy = b.prompts && isLegacyPromptsShape(b.prompts);
  const aPrompts = aPromptsLegacy ? undefined : a.prompts;
  const bPrompts = bPromptsLegacy ? undefined : b.prompts;

  return {
    ...a,
    ...b,
    prompts: mergePromptsState(aPrompts, bPrompts),
  };
}

function mergePromptsState(a: PromptsState | undefined, b: PromptsState | undefined): PromptsState {
  return {
    review: {
      unit: mergeSlugSurface(a?.review?.unit, b?.review?.unit),
      home: mergeHomeSurface(a?.review?.home, b?.review?.home),
      building: mergeSlugSurface(a?.review?.building, b?.review?.building),
      owner: mergeSlugSurface(a?.review?.owner, b?.review?.owner),
      pseo: mergeSlugSurface(a?.review?.pseo, b?.review?.pseo),
    },
    report: {
      unit: mergeSlugSurface(a?.report?.unit, b?.report?.unit),
      building: mergeSlugSurface(a?.report?.building, b?.report?.building),
    },
    appDownload: {
      home: mergeHomeSurface(a?.appDownload?.home, b?.appDownload?.home),
    },
  };
}

function mergeSlugSurface(
  a: SlugSurfaceState | undefined,
  b: SlugSurfaceState | undefined,
): SlugSurfaceState {
  return {
    count: Math.max(a?.count ?? 0, b?.count ?? 0),
    seen: unionArray(a?.seen, b?.seen),
    shown: Boolean(a?.shown || b?.shown),
  };
}

function mergeHomeSurface(
  a: HomeSurfaceState | undefined,
  b: HomeSurfaceState | undefined,
): HomeSurfaceState {
  const later = laterTimestamp(a?.last_view_at, b?.last_view_at);
  return {
    count: Math.max(a?.count ?? 0, b?.count ?? 0),
    // Conditional include — `exactOptionalPropertyTypes` disallows
    // assigning `undefined` to an optional string field.
    ...(later ? { last_view_at: later } : {}),
    shown: Boolean(a?.shown || b?.shown),
  };
}

function unionArray(a: string[] | undefined, b: string[] | undefined): string[] {
  const merged = new Set<string>();
  (a ?? []).forEach(s => merged.add(s));
  (b ?? []).forEach(s => merged.add(s));
  return Array.from(merged);
}

function laterTimestamp(a: string | undefined, b: string | undefined): string | undefined {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
}

/**
 * After a prompt fires on a surface, drop that surface's seen/last-
 * view tracking — the shown flag prevents future fires so we don't
 * need to keep growing the list. Trims payload size on long-running
 * accounts.
 */
export function purgeSeenIfShown(prompts: PromptsState): PromptsState {
  let review = prompts.review;
  if (review) {
    for (const surface of REVIEW_SURFACES) {
      // Annotate `bucket` explicitly: `review[surface]` widens to a
      // union across the four surface types, and without the hint
      // TS enters a circular-inference loop trying to type it
      // through `stripTracking`'s generic (TS7022).
      const bucket: SlugSurfaceState | HomeSurfaceState | undefined = review[surface];
      if (bucket?.shown) {
        review = { ...review, [surface]: stripTracking(bucket) };
      }
    }
  }

  let report = prompts.report;
  if (report) {
    for (const surface of REPORT_SURFACES) {
      const bucket: SlugSurfaceState | undefined = report[surface];
      if (bucket?.shown) {
        report = { ...report, [surface]: stripTracking(bucket) };
      }
    }
  }

  let appDownload = prompts.appDownload;
  if (appDownload) {
    for (const surface of APP_DOWNLOAD_SURFACES) {
      const bucket: HomeSurfaceState | undefined = appDownload[surface];
      if (bucket?.shown) {
        appDownload = { ...appDownload, [surface]: stripTracking(bucket) };
      }
    }
  }

  return {
    ...(review ? { review } : {}),
    ...(report ? { report } : {}),
    ...(appDownload ? { appDownload } : {}),
  };
}

/**
 * Remove per-surface tracking (seen list / last_view_at) once the
 * surface has fired — keep only `count` (for merge parity across
 * devices) and `shown`.
 */
function stripTracking<T extends SlugSurfaceState | HomeSurfaceState>(bucket: T): T {
  const stripped = { ...bucket } as T;
  if ('seen' in stripped) delete (stripped as SlugSurfaceState).seen;
  if ('last_view_at' in stripped) delete (stripped as HomeSurfaceState).last_view_at;
  return stripped;
}

/**
 * Detect a legacy flat-shape `prompts` object (pre-nested refactor).
 * We drop it on read/merge rather than migrate — few users have prompt
 * data yet, and the shape changed too much to translate cleanly.
 */
function isLegacyPromptsShape(prompts: unknown): boolean {
  if (!prompts || typeof prompts !== 'object') return false;
  const p = prompts as Record<string, unknown>;
  return 'counters' in p || 'seen' in p || 'shown' in p;
}
